import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import Footer from "../../../components/Footer/Footer";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import axios from "axios";
import { useSelector } from "react-redux";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiEndpoint = `${API_BASE_URL}/popage/indent`;

const POIndent = () => {
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const { role } = useSelector((state) => state.auth);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    projects: [],
    stages: [],
    types: [],
  });

  const navigate = useNavigate();

  // Single common API endpoint
  const getApiEndpoint = () => `${API_BASE_URL}/adminpage/poindent`;
  const getEditFields = () => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options:
        dropdownData.projects.map((project) => ({
          value: project.id,
          label: project.name,
        })) || [],
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options:
        dropdownData.types.map((type) => ({
          value: type.id,
          label: type.name,
        })) || [],
    },
  ];

  const fetchIndents = async () => {
    try {
      console.log("Fetching indents - START");
      console.log("Current user role:", role);

      const apiEndpoint = getApiEndpoint();
      console.log("Using API endpoint:", apiEndpoint);

      const response = await axios.get(apiEndpoint);
      console.log(
        "Raw API Response:",
        JSON.parse(JSON.stringify(response.data))
      );

      const formattedIndents = response.data
        .map((indent) => ({
          ...indent,
          L1: indent.L1 ? "Approved" : "Pending",
          L2: indent.L2 ? "Approved" : "Pending",
          PO: indent.PO ? "Approved" : "Pending",
          CreationDate: new Date(indent.CreationDate)
          .toLocaleDateString("en-GB")
          .replace(/\//g, "-"), // Format as dd-mm-yyyy
        }))
        .sort((a, b) => new Date(b.CreationDate) - new Date(a.CreationDate));

      console.log("Formatted Indents:", formattedIndents);
      console.log("Formatted Indents Length:", formattedIndents.length);

      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
    } catch (error) {
      console.error("Detailed Error Fetching Indents:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        role: role,
        endpoint: getApiEndpoint(),
      });
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      const dropdownDataState = {
        users: usersRes.data.map((user) => ({
          id: user.UserID,
          name: user.Username,
        })),
        projects: projectsRes.data.map((project) => ({
          id: project.ProjectID,
          name: project.ProjectName,
        })),
        stages: stagesRes.data.map((stage) => ({
          id: stage.StageID,
          name: stage.StageName,
        })),
        types: typesRes.data.map((type) => ({
          id: type.TypeID,
          name: type.TypeName,
        })),
      };

      setDropdownData(dropdownDataState);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    fetchIndents();
    fetchDropdownData();
  }, []);

  const handleSearch = (query) => {
    if (!query) {
      setFilteredIndents(indents);
    } else {
      const lowercaseQuery = query.toLowerCase();
      const filtered = indents.filter((indent) => {
        return (
          indent.IndentID.toString().toLowerCase().includes(lowercaseQuery) ||
          (indent.Status &&
            indent.Status.toLowerCase().includes(lowercaseQuery)) ||
          (indent.ProjectName &&
            indent.ProjectName.toLowerCase().includes(lowercaseQuery)) ||
          (indent.TypeName &&
            indent.TypeName.toLowerCase().includes(lowercaseQuery)) ||
          (indent.CreationDate &&
            indent.CreationDate.toString()
              .toLowerCase()
              .includes(lowercaseQuery))
        );
      });
      setFilteredIndents(filtered);
    }
  };

  const handleDateFilter = (filterOptions) => {
    const { dateFrom, dateTo } = filterOptions;
    if (dateFrom && dateTo) {
      const filtered = indents.filter((indent) => {
        const creationDate = new Date(indent.CreationDate);
        return (
          creationDate >= new Date(dateFrom) && creationDate <= new Date(dateTo)
        );
      });
      setFilteredIndents(
        filtered.sort(
          (a, b) => new Date(b.CreationDate) - new Date(a.CreationDate)
        )
      );
    } else {
      setFilteredIndents(indents);
    }
  };

  const handleRedirect = (page) => {
    navigate(page);
  };

  const handleRowClick = (row) => {
    navigate(`/POtable`, { state: { row } });
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">PD Table</h3>
        </div>

        {dropdownData.users.length > 0 && dropdownData.projects.length > 0 && (
          <ActionBar
            title="Indent"
            fields={getEditFields()}
            apiEndpoint={apiEndpoint}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={fetchIndents}
            onSearch={handleSearch}
            onFilter={handleDateFilter}
            rowKey="IndentID"
            userRole={role}
            addLink="/addl2Indent"
            editRoute="/POTable" // Added editRoute prop with default value
          />
        )}

        <div className="mt-4 overflow-x-auto">
          <DynamicTable
            columns={[
              { key: "IndentID", label: "Indent ID" },
              { key: "CreationDate", label: "Creation Date" },
              { key: "ProjectName", label: "Project" },
              { key: "TypeName", label: "Indent Type" },
              { key: "TaskSchedule", label: "Task Scheduled" },
              { key: "Status", label: "Status" },
              { key: "ItemCount", label: "Item Count" },
            ]}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={(row) => setSelectedRow(row)}
            onDoubleClick={(row) => handleRowClick(row)}
            rowKey="IndentID"
            userIdKey="UserID"
            highlightColor="bg-green-100"
            noDataMessage="No indents found."
            userRole="admin"
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default POIndent;
